import type {
    SVGProps
} from "react";
import {
    useAnatomyStyle
} from "./style";
import {
    type AnatomyBalloon,
    type AnatomySegment,
    type AnatomyLayout,
    type AnatomyChain,
    type AnatomyGhost,
    type AnatomyPoint,
    LINE_HEIGHT,
    formatValue,
    RULER_SIZE,
    LABEL_GAP
} from "./layout";
import {
    type AnatomyMeasurement,
    getParentInnerRect,
    type AnatomyPart,
    type AnatomyRect,
    isCircle
} from "./measure";
import {
    findSizeTokens,
    isFullRadius
} from "./tokens";

export type AnatomyPointer = {
    x: number;
    y: number;
};

const END_BAR = 4;

const EXTENSION_GAP = 3;

const EXTENSION_OVERSHOOT = 5;

const CENTER_OVERSHOOT = 6;

const MAJOR_EVERY = 4;

const MIN_STATE_LABEL_SIZE = 36;

const FOCUS_CHAR_WIDTH = 6.4;

const BALLOON_RADIUS = 8;

const toScreenRect = (rect: AnatomyRect, layout: AnatomyLayout): AnatomyRect => ({
    x: layout.originX + rect.x * layout.scale,
    y: layout.originY + rect.y * layout.scale,
    height: rect.height * layout.scale,
    width: rect.width * layout.scale
});

const getUnevenRadii = (part: AnatomyPart, rect: AnatomyRect, scale: number) => {
    if(!part.radii || part.radii.every((value) => value === part.radii?.[0])) {
        return null;
    }

    const limit = Math.min(rect.width, rect.height) / 2;

    return part.radii.map((value) => Math.min(value, limit) * scale);
};

const getOutlinePath = (screen: AnatomyRect, radii: Array<number>) => {
    const [
        topLeft = 0,
        topRight = 0,
        bottomRight = 0,
        bottomLeft = 0
    ] = radii;

    const right = screen.x + screen.width;

    const bottom = screen.y + screen.height;

    return [
        `M ${screen.x + topLeft} ${screen.y}`,
        `H ${right - topRight}`,
        `A ${topRight} ${topRight} 0 0 1 ${right} ${screen.y + topRight}`,
        `V ${bottom - bottomRight}`,
        `A ${bottomRight} ${bottomRight} 0 0 1 ${right - bottomRight} ${bottom}`,
        `H ${screen.x + bottomLeft}`,
        `A ${bottomLeft} ${bottomLeft} 0 0 1 ${screen.x} ${bottom - bottomLeft}`,
        `V ${screen.y + topLeft}`,
        `A ${topLeft} ${topLeft} 0 0 1 ${screen.x + topLeft} ${screen.y}`,
        "Z"
    ].join(" ");
};

const getCornerRadius = (part: AnatomyPart, rect: AnatomyRect, scale: number) => {
    if(isFullRadius(part.radius, rect.width, rect.height)) {
        return Math.min(rect.width, rect.height) / 2 * scale;
    }

    return Math.min(part.radius, rect.width / 2, rect.height / 2) * scale;
};

const TEXT_STYLE = {
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
    overflow: "visible"
} as const;

const HIT_STYLE = {
    pointerEvents: "all",
    cursor: "default"
} as const;

const LAYER_STYLE = {
    position: "absolute",
    overflow: "visible",
    left: 0,
    top: 0
} as const;

const isActiveSegment = (segment: AnatomySegment, activeSegmentKey: string | null, activePartId: number | null) => segment.key === activeSegmentKey || activePartId !== null && segment.kind !== "gap" && segment.kind !== "inset" && segment.partIds.includes(activePartId);

export const AnatomyGrid = ({
    height,
    layout,
    width,
    id
}: {
    layout: AnatomyLayout;
    height: number;
    width: number;
    id: string;
}) => {
    const minor = layout.gridStep * layout.scale;

    const major = minor * MAJOR_EVERY;

    const style = useAnatomyStyle();

    return <svg
        viewBox={`0 0 ${width} ${height}`}
        style={LAYER_STYLE}
        aria-hidden={true}
        height={height}
        width={width}
    >
        <defs>
            <pattern
                patternUnits="userSpaceOnUse"
                id={`${id}-minor`}
                x={layout.originX}
                y={layout.originY}
                height={minor}
                width={minor}
            >
                <path
                    d={`M ${minor} 0 L 0 0 0 ${minor}`}
                    strokeWidth={style.width.thin}
                    stroke={style.color.grid}
                    fill="none"
                />
            </pattern>
            <pattern
                patternUnits="userSpaceOnUse"
                id={`${id}-major`}
                x={layout.originX}
                y={layout.originY}
                height={major}
                width={major}
            >
                <path
                    d={`M ${major} 0 L 0 0 0 ${major}`}
                    stroke={style.color.gridMajor}
                    strokeWidth={style.width.thin}
                    fill="none"
                />
            </pattern>
        </defs>
        <rect
            fill={`url(#${id}-minor)`}
            height={height}
            width={width}
        />
        <rect
            fill={`url(#${id}-major)`}
            height={height}
            width={width}
        />
    </svg>;
};

export const AnatomyHatch = ({
    measurement,
    isActive,
    layout,
    id
}: {
    measurement: AnatomyMeasurement;
    layout: AnatomyLayout;
    isActive: boolean;
    id: string;
}) => {
    const style = useAnatomyStyle();

    if(!measurement.hitRect) {
        return null;
    }

    const hit = toScreenRect(measurement.hitRect, layout);

    return <g>
        <defs>
            <pattern
                patternTransform="rotate(45)"
                patternUnits="userSpaceOnUse"
                id={`${id}-hatch`}
                height={6}
                width={6}
            >
                <line
                    stroke={isActive ? style.color.accent : style.color.hatch}
                    strokeWidth={style.width.thin}
                    y2={6}
                    x1={0}
                    x2={0}
                    y1={0}
                />
            </pattern>
            <mask
                id={`${id}-hatch-mask`}
            >
                <rect
                    height={hit.height}
                    width={hit.width}
                    fill="white"
                    x={hit.x}
                    y={hit.y}
                />
                {
                    measurement.parts.map((part) => {
                        const rect = toScreenRect(part.rect, layout);

                        return <rect
                            rx={getCornerRadius(part, part.rect, layout.scale)}
                            height={rect.height + 4}
                            width={rect.width + 4}
                            x={rect.x - 2}
                            y={rect.y - 2}
                            key={part.id}
                            fill="black"
                        />;
                    })
                }
            </mask>
        </defs>
        <rect
            mask={`url(#${id}-hatch-mask)`}
            fill={`url(#${id}-hatch)`}
            height={hit.height}
            width={hit.width}
            x={hit.x}
            y={hit.y}
        />
    </g>;
};

const AnatomyText = ({
    isActive,
    layout,
    rect,
    part
}: {
    layout: AnatomyLayout;
    isActive: boolean;
    rect: AnatomyRect;
    part: AnatomyPart;
}) => {
    const style = useAnatomyStyle();

    const screen = toScreenRect(rect, layout);

    const firstLine = part.textLines[0];

    const isField = part.lineHeight === null;

    const fontSize = (part.fontSize ?? 14) * layout.scale;

    return <foreignObject
        height={screen.height + fontSize}
        width={screen.width + 2}
        style={TEXT_STYLE}
        x={screen.x}
        y={screen.y}
    >
        <div
            style={{
                textAlign: isField ? "start" : part.textAlign === "center" || part.textAlign === "right" || part.textAlign === "end" ? part.textAlign : "start",
                lineHeight: isField ? `${screen.height}px` : `${(part.lineHeight ?? 20) * layout.scale}px`,
                paddingLeft: isField && firstLine ? (firstLine.x - rect.x) * layout.scale : 0,
                whiteSpace: part.textLines.length <= 1 ? "nowrap" : "pre-wrap",
                color: isActive ? style.color.accent : style.color.ink,
                letterSpacing: part.letterSpacing * layout.scale,
                fontFamily: part.fontFamily ?? undefined,
                fontWeight: part.fontWeight ?? 400,
                height: screen.height,
                width: screen.width,
                fontSize
            }}
        >
            {part.text}
        </div>
    </foreignObject>;
};

const AnatomyShape = ({
    isActive = false,
    isGhost = false,
    layout,
    rect,
    part
}: {
    layout: AnatomyLayout;
    isActive?: boolean;
    isGhost?: boolean;
    rect: AnatomyRect;
    part: AnatomyPart;
}) => {
    const screen = toScreenRect(rect, layout);

    const style = useAnatomyStyle();

    const stroke = isActive ? style.color.accent : isGhost ? style.color.line : style.color.ink;

    const strokeWidth = isActive ? style.width.strong : style.width.thin;

    const strokeDasharray = isGhost ? style.dash.ghost : undefined;

    if(part.kind === "text") {
        return <g>
            {
                isGhost || isActive ?
                    <rect
                        stroke={isActive ? style.color.accent : style.color.line}
                        strokeDasharray={style.dash.dotted}
                        strokeWidth={style.width.thin}
                        height={screen.height}
                        width={screen.width}
                        x={screen.x}
                        y={screen.y}
                        fill="none"
                    />
                :
                    null
            }
            {
                isGhost ?
                    null
                :
                    <AnatomyText
                        isActive={isActive}
                        layout={layout}
                        rect={rect}
                        part={part}
                    />
            }
        </g>;
    }

    const transform = part.rotation ? `rotate(${part.rotation} ${screen.x + screen.width / 2} ${screen.y + screen.height / 2})` : undefined;

    const unevenRadii = getUnevenRadii(part, rect, layout.scale);

    if(part.kind === "icon") {
        return <g
            transform={transform}
        >
            <svg
                strokeWidth={isGhost ? style.width.thin : part.iconStroke?.width}
                vectorEffect={isGhost ? "non-scaling-stroke" : undefined}
                strokeDasharray={isGhost ? style.dash.ghost : undefined}
                strokeLinejoin={part.iconStroke?.linejoin}
                strokeLinecap={part.iconStroke?.linecap}
                viewBox={part.iconViewBox ?? undefined}
                preserveAspectRatio="xMidYMid meet"
                dangerouslySetInnerHTML={{
                    __html: part.iconMarkup ?? ""
                }}
                style={{
                    overflow: "visible"
                }}
                height={screen.height}
                stroke="currentColor"
                width={screen.width}
                color={stroke}
                x={screen.x}
                y={screen.y}
                fill="none"
            />
        </g>;
    }

    return <g
        strokeDasharray={strokeDasharray}
        strokeWidth={strokeWidth}
        transform={transform}
        stroke={stroke}
        fill="none"
    >
        {
            unevenRadii ?
                <path
                    d={getOutlinePath(screen, unevenRadii)}
                />
            :
                <rect
                    rx={getCornerRadius(part, rect, layout.scale)}
                    height={screen.height}
                    width={screen.width}
                    x={screen.x}
                    y={screen.y}
                />
        }
        {
            part.kind === "media" ?
                <path
                    d={`M ${screen.x} ${screen.y} L ${screen.x + screen.width} ${screen.y + screen.height} M ${screen.x + screen.width} ${screen.y} L ${screen.x} ${screen.y + screen.height}`}
                />
            :
                null
        }
    </g>;
};

const AnatomyCenterMarks = ({
    layout,
    rect,
    part
}: {
    layout: AnatomyLayout;
    rect: AnatomyRect;
    part: AnatomyPart;
}) => {
    const style = useAnatomyStyle();

    if(part.kind !== "shape" && part.kind !== "media") {
        return null;
    }

    const screen = toScreenRect(rect, layout);

    const centerX = screen.x + screen.width / 2;

    const centerY = screen.y + screen.height / 2;

    if(isCircle(part)) {
        return <g
            strokeDasharray={style.dash.center}
            strokeWidth={style.width.thin}
            stroke={style.color.line}
        >
            <line
                x2={screen.x + screen.width + CENTER_OVERSHOOT}
                x1={screen.x - CENTER_OVERSHOOT}
                y1={centerY}
                y2={centerY}
            />
            <line
                y2={screen.y + screen.height + CENTER_OVERSHOOT}
                y1={screen.y - CENTER_OVERSHOOT}
                x1={centerX}
                x2={centerX}
            />
        </g>;
    }

    if(!isFullRadius(part.radius, rect.width, rect.height) || part.childIds.length > 0) {
        return null;
    }

    const isWide = screen.width >= screen.height;

    return <g
        strokeDasharray={style.dash.center}
        strokeWidth={style.width.thin}
        stroke={style.color.line}
    >
        {
            isWide ?
                <line
                    x2={screen.x + screen.width + CENTER_OVERSHOOT}
                    x1={screen.x - CENTER_OVERSHOOT}
                    y1={centerY}
                    y2={centerY}
                />
            :
                <line
                    y2={screen.y + screen.height + CENTER_OVERSHOOT}
                    y1={screen.y - CENTER_OVERSHOOT}
                    x1={centerX}
                    x2={centerX}
                />
        }
    </g>;
};

const AnatomyStateLabel = ({
    layout,
    label,
    rect
}: {
    layout: AnatomyLayout;
    rect: AnatomyRect;
    label: string;
}) => {
    const screen = toScreenRect(rect, layout);

    const style = useAnatomyStyle();

    if(!label || screen.width < MIN_STATE_LABEL_SIZE) {
        return null;
    }

    return <text
        fontFamily={style.text.small.fontFamily}
        fontWeight={style.text.small.fontWeight}
        fontSize={style.text.small.fontSize}
        y={screen.y + screen.height / 2}
        x={screen.x + screen.width / 2}
        dominantBaseline="central"
        fill={style.color.line}
        textAnchor="middle"
    >
        {label}
    </text>;
};

export const AnatomyObject = ({
    activePartIds,
    activeState,
    measurement,
    stateLabels,
    ghosts,
    layout
}: {
    measurement: AnatomyMeasurement;
    activePartIds: Array<number>;
    ghosts: Array<AnatomyGhost>;
    stateLabels: Array<string>;
    layout: AnatomyLayout;
    activeState: number;
}) => {
    const root = toScreenRect({
        height: measurement.height,
        width: measurement.width,
        x: 0,
        y: 0
    }, layout);

    const ghostPartIds = new Set(ghosts.map((ghost) => ghost.partId));

    const style = useAnatomyStyle();

    return <g>
        <rect
            strokeDasharray={style.dash.bounds}
            strokeWidth={style.width.thin}
            stroke={style.color.faint}
            height={root.height}
            width={root.width}
            fill="none"
            x={root.x}
            y={root.y}
        />
        {
            ghosts.map((ghost) => {
                const part = measurement.parts[ghost.partId];

                return part ? <g
                    key={`ghost-${ghost.stateIndex}-${ghost.partId}`}
                >
                    <AnatomyShape
                        rect={ghost.rect}
                        layout={layout}
                        isGhost={true}
                        part={part}
                    />
                    <AnatomyCenterMarks
                        rect={ghost.rect}
                        layout={layout}
                        part={part}
                    />
                    <AnatomyStateLabel
                        label={stateLabels[ghost.stateIndex] ?? ""}
                        rect={ghost.rect}
                        layout={layout}
                    />
                </g> : null;
            })
        }
        {
            measurement.parts.map((part) => <g
                key={part.id}
            >
                <AnatomyShape
                    isActive={activePartIds.includes(part.id)}
                    rect={part.rect}
                    layout={layout}
                    part={part}
                />
                <AnatomyCenterMarks
                    rect={part.rect}
                    layout={layout}
                    part={part}
                />
                {
                    ghostPartIds.has(part.id) ?
                        <AnatomyStateLabel
                            label={stateLabels[activeState] ?? ""}
                            rect={part.rect}
                            layout={layout}
                        />
                    :
                        null
                }
            </g>)
        }
    </g>;
};

export const AnatomyChainView = ({
    onActiveSegmentChange,
    activeSegmentKey,
    activePartId,
    chain
}: {
    onActiveSegmentChange: (key: string | null) => void;
    activeSegmentKey: string | null;
    activePartId: number | null;
    chain: AnatomyChain;
}) => {
    const isHorizontal = chain.side === "top" || chain.side === "bottom";

    const direction = chain.side === "top" || chain.side === "left" ? -1 : 1;

    const toPoint = (along: number, cross: number): AnatomyPoint => isHorizontal ? {
        x: along,
        y: cross
    } : {
        x: cross,
        y: along
    };

    const style = useAnatomyStyle();

    const labelStyle = {
        strokeWidth: style.width.halo,
        stroke: style.color.stage,
        strokeLinejoin: "round",
        paintOrder: "stroke"
    } as const;

    const renderLine = (key: string, props: SVGProps<SVGLineElement>, from: AnatomyPoint, to: AnatomyPoint) => <line
        {...props}
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        key={key}
    />;

    const first = chain.boundaries[0] ?? 0;

    const last = chain.boundaries[chain.boundaries.length - 1] ?? first;

    const getLabelPosition = (segment: AnatomySegment, line: number) => {
        const offset = LABEL_GAP + segment.label.row * chain.rowHeight;

        const tokenShift = segment.label.token ? LINE_HEIGHT : 0;

        const inward = line === 0 ? 0 : LINE_HEIGHT;

        if(chain.side === "top" || chain.side === "left") {
            return chain.line - offset - tokenShift - 3 + inward;
        }

        return chain.line + offset + 9 + inward;
    };

    const guideProps = {
        strokeDasharray: style.dash.dotted,
        strokeWidth: style.width.thin,
        stroke: style.color.faint
    };

    const centerProps = {
        strokeDasharray: style.dash.center,
        strokeWidth: style.width.thin,
        stroke: style.color.line
    };

    const lineProps = {
        strokeWidth: style.width.thin,
        stroke: style.color.line
    };

    const activeProps = {
        strokeWidth: style.width.strong,
        stroke: style.color.accent
    };

    return <g>
        {
            chain.guides.map((guide, index) => renderLine(`guide-${index}`, guide.isCenter ? centerProps : guideProps, toPoint(guide.position, guide.from), toPoint(guide.position, guide.to)))
        }
        {
            chain.boundaries.map((boundary, index) => renderLine(`extension-${index}`, {
                strokeWidth: style.width.thin,
                stroke: style.color.faint
            }, toPoint(boundary, chain.edge + direction * EXTENSION_GAP), toPoint(boundary, chain.line + direction * EXTENSION_OVERSHOOT)))
        }
        {renderLine("dimension", lineProps, toPoint(first, chain.line), toPoint(last, chain.line))}
        {
            chain.boundaries.map((boundary, index) => renderLine(`bar-${index}`, lineProps, toPoint(boundary, chain.line - END_BAR), toPoint(boundary, chain.line + END_BAR)))
        }
        {
            chain.segments.map((segment) => {
                const isActive = isActiveSegment(segment, activeSegmentKey, activePartId);

                const center = segment.label.center;

                const valueLine = getLabelPosition(segment, 0);

                const tokenLine = getLabelPosition(segment, 1);

                const valueText = segment.label.prefix ? `${segment.label.prefix} ${segment.label.value}` : segment.label.value;

                return <g
                    onPointerEnter={() => onActiveSegmentChange(segment.key)}
                    onPointerLeave={() => onActiveSegmentChange(null)}
                    style={HIT_STYLE}
                    key={segment.key}
                >
                    {
                        renderLine("hit", {
                            strokeWidth: style.width.hit,
                            stroke: "transparent",
                            pointerEvents: "all"
                        }, toPoint(segment.start, chain.line), toPoint(segment.end, chain.line))
                    }
                    {
                        isActive ?
                            <g>
                                {renderLine("active", activeProps, toPoint(segment.start, chain.line), toPoint(segment.end, chain.line))}
                                {renderLine("start", activeProps, toPoint(segment.start, chain.line - END_BAR - 1), toPoint(segment.start, chain.line + END_BAR + 1))}
                                {renderLine("end", activeProps, toPoint(segment.end, chain.line - END_BAR - 1), toPoint(segment.end, chain.line + END_BAR + 1))}
                            </g>
                        :
                            null
                    }
                    <text
                        transform={isHorizontal ? undefined : `rotate(-90 ${valueLine} ${center})`}
                        fill={isActive ? style.color.accent : style.color.ink}
                        fontFamily={style.text.label.fontFamily}
                        fontWeight={style.text.label.fontWeight}
                        x={isHorizontal ? center : valueLine}
                        y={isHorizontal ? valueLine : center}
                        fontSize={style.text.label.fontSize}
                        textAnchor="middle"
                        style={labelStyle}
                    >
                        {valueText}
                    </text>
                    {
                        segment.label.token ?
                            <text
                                transform={isHorizontal ? undefined : `rotate(-90 ${tokenLine} ${center})`}
                                fill={isActive ? style.color.accent : style.color.ink}
                                fontFamily={style.text.small.fontFamily}
                                fontWeight={style.text.small.fontWeight}
                                x={isHorizontal ? center : tokenLine}
                                y={isHorizontal ? tokenLine : center}
                                fontSize={style.text.small.fontSize}
                                textAnchor="middle"
                                style={labelStyle}
                            >
                                {segment.label.token}
                            </text>
                        :
                            null
                    }
                </g>;
            })
        }
    </g>;
};

export const AnatomyBalloons = ({
    onActiveBalloonChange,
    activeBalloon,
    balloons
}: {
    onActiveBalloonChange: (partId: number | null | undefined) => void;
    activeBalloon: number | null | undefined;
    balloons: Array<AnatomyBalloon>;
}) => {
    const style = useAnatomyStyle();

    return <g>
        {
            balloons.map((item) => {
                const isActive = activeBalloon === item.partId;

                const stroke = isActive ? style.color.accent : style.color.line;

                return <g
                    onPointerEnter={() => onActiveBalloonChange(item.partId)}
                    onPointerLeave={() => onActiveBalloonChange(undefined)}
                    key={item.partId ?? "hit"}
                    style={HIT_STYLE}
                >
                    <g>
                        <line
                            strokeWidth={isActive ? style.width.strong : style.width.thin}
                            x2={item.balloon.x}
                            y2={item.balloon.y}
                            x1={item.anchor.x}
                            y1={item.anchor.y}
                            stroke={stroke}
                            fill="none"
                        />
                        <circle
                            cx={item.anchor.x}
                            cy={item.anchor.y}
                            fill={stroke}
                            r={1.75}
                        />
                        <circle
                            fill={isActive ? style.color.accent : style.color.stage}
                            strokeWidth={style.width.thin}
                            cx={item.balloon.x}
                            cy={item.balloon.y}
                            r={BALLOON_RADIUS}
                            stroke={stroke}
                        />
                        <text
                            fill={isActive ? style.color.stage : style.color.ink}
                            fontFamily={style.text.label.fontFamily}
                            fontWeight={style.text.label.fontWeight}
                            fontSize={style.text.label.fontSize}
                            dominantBaseline="central"
                            textAnchor="middle"
                            y={item.balloon.y}
                            x={item.balloon.x}
                        >
                            {item.number}
                        </text>
                    </g>
                </g>;
            })
        }
    </g>;
};

export const AnatomyFocus = ({
    measurement,
    layout,
    part
}: {
    measurement: AnatomyMeasurement;
    layout: AnatomyLayout;
    part: AnatomyPart;
}) => {
    const style = useAnatomyStyle();

    const parentRect = getParentInnerRect(part, measurement);

    const rect = toScreenRect(part.rect, layout);

    const parent = toScreenRect(parentRect, layout);

    const centerX = rect.x + rect.width / 2;

    const centerY = rect.y + rect.height / 2;

    const insets = [
        {
            value: part.rect.x - parentRect.x,
            from: {
                x: parent.x,
                y: centerY
            },
            to: {
                x: rect.x,
                y: centerY
            },
            key: "left"
        },
        {
            value: parentRect.x + parentRect.width - part.rect.x - part.rect.width,
            to: {
                x: parent.x + parent.width,
                y: centerY
            },
            from: {
                x: rect.x + rect.width,
                y: centerY
            },
            key: "right"
        },
        {
            value: part.rect.y - parentRect.y,
            from: {
                x: centerX,
                y: parent.y
            },
            to: {
                x: centerX,
                y: rect.y
            },
            key: "top"
        },
        {
            value: parentRect.y + parentRect.height - part.rect.y - part.rect.height,
            to: {
                y: parent.y + parent.height,
                x: centerX
            },
            from: {
                y: rect.y + rect.height,
                x: centerX
            },
            key: "bottom"
        }
    ].filter((inset) => inset.value >= 0.5);

    const getInsetText = (value: number) => {
        const token = findSizeTokens("space", value)[0];

        return token ? `${formatValue(value)}PX ${token}` : `${formatValue(value)}PX`;
    };

    const labelProps = {
        fontFamily: style.text.label.fontFamily,
        fontWeight: style.text.label.fontWeight,
        fontSize: style.text.label.fontSize,
        strokeWidth: style.width.halo,
        stroke: style.color.stage,
        fill: style.color.accent,
        strokeLinejoin: "round",
        paintOrder: "stroke"
    } as const;

    return <g>
        {
            insets.map((inset) => {
                const isVertical = inset.key === "top" || inset.key === "bottom";

                const middleX = (inset.from.x + inset.to.x) / 2;

                const middleY = (inset.from.y + inset.to.y) / 2;

                const text = getInsetText(inset.value);

                const isOutside = !isVertical && Math.abs(inset.to.x - inset.from.x) < text.length * FOCUS_CHAR_WIDTH + 8;

                let labelX = middleX;

                let anchor: "start" | "middle" | "end" = "middle";

                if(isVertical) {
                    labelX = middleX + 6;

                    anchor = "start";
                } else if(isOutside && inset.key === "left") {
                    labelX = inset.from.x - 6;

                    anchor = "end";
                } else if(isOutside) {
                    labelX = inset.to.x + 6;

                    anchor = "start";
                }

                return <g
                    key={inset.key}
                >
                    <line
                        strokeWidth={style.width.thin}
                        stroke={style.color.accent}
                        x1={inset.from.x}
                        y1={inset.from.y}
                        x2={inset.to.x}
                        y2={inset.to.y}
                    />
                    <text
                        dominantBaseline={isVertical || isOutside ? "central" : undefined}
                        y={isVertical || isOutside ? middleY : middleY - 5}
                        {...labelProps}
                        textAnchor={anchor}
                        x={labelX}
                    >
                        {text}
                    </text>
                </g>;
            })
        }
        <text
            {...labelProps}
            x={rect.x + rect.width + 6}
            textAnchor="start"
            y={rect.y - 6}
        >
            {`${formatValue(part.rect.width)} × ${formatValue(part.rect.height)}PX`}
        </text>
    </g>;
};

export const AnatomyRulers = ({
    activeSegment,
    pointer,
    height,
    layout,
    width
}: {
    pointer: AnatomyPointer | null;
    activeSegment: |
    {
        isHorizontal: boolean;
        start: number;
        end: number;
    } |
    null;
    layout: AnatomyLayout;
    height: number;
    width: number;
}) => {
    const style = useAnatomyStyle();

    const step = layout.gridStep * layout.scale;

    const textProps = {
        fontFamily: style.text.small.fontFamily,
        fontWeight: style.text.small.fontWeight,
        fontSize: style.text.small.fontSize,
        fill: style.color.line
    };

    const getTickProps = (isMajor: boolean) => ({
        stroke: isMajor ? style.color.line : style.color.faint,
        strokeWidth: style.width.thin
    });

    const getTicks = (origin: number, end: number) => {
        const ticks: Array<{
            position: number;
            isMajor: boolean;
            value: number;
        }> = [];

        const firstIndex = Math.ceil((RULER_SIZE - origin) / step);

        const lastIndex = Math.floor((end - origin) / step);

        for(let index = firstIndex; index <= lastIndex; index += 1) {
            ticks.push({
                isMajor: index % MAJOR_EVERY === 0,
                position: origin + index * step,
                value: index * layout.gridStep
            });
        }

        return ticks;
    };

    const horizontalTicks = getTicks(layout.originX, width - 8);

    const verticalTicks = getTicks(layout.originY, height - 8);

    const pointerX = pointer ? layout.originX + pointer.x * layout.scale : null;

    const pointerY = pointer ? layout.originY + pointer.y * layout.scale : null;

    return <g>
        {
            activeSegment ?
                <rect
                    height={activeSegment.isHorizontal ? 6 : activeSegment.end - activeSegment.start}
                    width={activeSegment.isHorizontal ? activeSegment.end - activeSegment.start : 6}
                    x={activeSegment.isHorizontal ? activeSegment.start : 0}
                    y={activeSegment.isHorizontal ? 0 : activeSegment.start}
                    fill={style.color.hatch}
                />
            :
                null
        }
        {
            horizontalTicks.map((tick) => <g
                key={`h-${tick.position}`}
            >
                <line
                    {...getTickProps(tick.isMajor)}
                    y2={tick.isMajor ? 8 : 4}
                    x1={tick.position}
                    x2={tick.position}
                    y1={0}
                />
                {
                    tick.isMajor ?
                        <text
                            {...textProps}
                            textAnchor="middle"
                            x={tick.position}
                            y={18}
                        >
                            {tick.value}
                        </text>
                    :
                        null
                }
            </g>)
        }
        {
            verticalTicks.map((tick) => <g
                key={`v-${tick.position}`}
            >
                <line
                    {...getTickProps(tick.isMajor)}
                    x2={tick.isMajor ? 8 : 4}
                    y1={tick.position}
                    y2={tick.position}
                    x1={0}
                />
                {
                    tick.isMajor ?
                        <text
                            transform={`rotate(-90 18 ${tick.position})`}
                            {...textProps}
                            textAnchor="middle"
                            y={tick.position}
                            x={18}
                        >
                            {tick.value}
                        </text>
                    :
                        null
                }
            </g>)
        }
        {
            pointer && pointerX !== null && pointerY !== null ?
                <g
                    strokeWidth={style.width.thin}
                    stroke={style.color.accent}
                >
                    <line
                        x1={pointerX}
                        x2={pointerX}
                        y2={12}
                        y1={0}
                    />
                    <line
                        y1={pointerY}
                        y2={pointerY}
                        x2={12}
                        x1={0}
                    />
                </g>
            :
                null
        }
    </g>;
};
